/**
 * Everything one skill opens, worked out from what the client already holds.
 *
 * Its own file rather than sitting beside the window that draws it, so the
 * window is only a window: a file that exports both a component and the thing
 * it is drawn from cannot be swapped in while the game is running.
 */

import type {
  ItemDefinitionWire,
  ObjectDefinitionWire,
  QuestWire,
  RecipeWire,
} from '@inverse/protocol';

export interface GuideEntry {
  level: number;
  /** What comes out of it. */
  name: string;
  /** What it takes: ingredients, other skills, a quest. */
  needs: string[];
  /** Where it happens, or empty for anything done in the bag. */
  where: string;
  /** What it gives, in experience. */
  experience: number;
  /** Whether this player is good enough for it yet. */
  reached: boolean;
}

/**
 * Everything one skill opens, in level order.
 *
 * Recipes and the things gathered with that skill both count: a guide that
 * listed what to smith but not what to mine would be half a guide.
 */
export function guideFor(
  skill: string,
  level: number,
  {
    recipes,
    items,
    objects,
    quests,
    yields,
  }: {
    recipes: readonly RecipeWire[];
    items: readonly ItemDefinitionWire[];
    objects: readonly ObjectDefinitionWire[];
    quests: readonly QuestWire[];
    yields: readonly { objectId: number; itemId: number }[];
  },
): GuideEntry[] {
  const named = (itemId: number): string =>
    items.find((item) => item.id === itemId)?.name ?? `Item ${itemId}`;
  const place = (objectId: number): string =>
    objects.find((object) => object.id === objectId)?.name ?? '';

  const entries: GuideEntry[] = [];

  for (const recipe of recipes) {
    if (recipe.skill !== skill || recipe.hidden) continue;

    const needs = [`${recipe.inputQuantity} x ${named(recipe.inputId)}`];
    for (const also of recipe.alsoNeeds) needs.push(`${also.skill} ${also.level}`);

    if (recipe.locked?.quest) {
      const quest = quests.find((one) => one.id === recipe.locked?.quest);
      needs.push(quest ? `Quest: ${quest.name}` : 'A quest');
    }
    if (recipe.locked?.flag) needs.push('Something done first');

    // Everywhere it can be made, since the same thing is often made in more
    // than one place and a guide that named one of them would send people to
    // the wrong side of the map.
    const places = [recipe.objectId, ...recipe.alsoAt].map(place).filter((one) => one.length > 0);

    entries.push({
      level: recipe.level,
      name: `${recipe.outputQuantity > 1 ? `${recipe.outputQuantity} x ` : ''}${named(recipe.outputId)}`,
      needs,
      where: places.join(' or '),
      experience: recipe.experience,
      reached: level >= recipe.level,
    });
  }

  // And what is gathered with it: the level and the experience belong to the
  // item, so anything carrying them is something somebody gathers.
  for (const item of items) {
    if (item.gatherLevel <= 0) continue;
    if (!item.tags.some((tag) => tag.trim() === `yield_${skill}`)) continue;

    const from = yields
      .filter((pair) => pair.itemId === item.id)
      .map((pair) => place(pair.objectId))
      .filter((one) => one.length > 0);

    entries.push({
      level: item.gatherLevel,
      name: item.name,
      needs: [],
      where: [...new Set(from)].join(' or '),
      experience: item.gatherExperience,
      reached: level >= item.gatherLevel,
    });
  }

  return entries.sort((a, b) => a.level - b.level || a.name.localeCompare(b.name));
}
